/**
 * Parser/printer for MedDream's "Condition query text form".
 *
 * MedDream shows every condition block both as a visual query builder and as
 * text, kept in sync:
 *
 *   modalities_in_study not_contain "MG" && modalities_in_study not_contain "OPT"
 *   (modalities_in_study contain "CT" || modalities_in_study contain "MR")
 *   series_number = 1
 *   1 == 1                       <- the always-true idiom for an empty block
 *
 * Grammar:
 *   expr    := orExpr
 *   orExpr  := andExpr ('||' andExpr)*
 *   andExpr := unary ('&&' unary)*
 *   unary   := '!' unary | primary
 *   primary := '(' expr ')' | '1' '==' '1' | field operator value
 *   value   := '"' ... '"' | number
 *
 * Caveat: `id` and `path` are not representable in the text form. Re-parsing an
 * edited string regenerates ids and falls back to registry default paths, which
 * differs from the source for the two fields MedDream exports with variant
 * paths. Callers editing an existing tree should reconcile against the original
 * rather than replacing it wholesale.
 */

import {
  ALL_KNOWN_FIELDS,
  COMBINATOR,
  COMBINATOR_TO_DSL,
  DSL_TO_COMBINATOR,
  INPUT_TYPE,
  getDefaultPath,
  getInputType,
} from "./fieldRegistry";

export const TRUE_LITERAL = "1 == 1";

const SYMBOL_OPERATORS = ["==", ">=", "<=", "!=", "=", ">", "<"];
const WORD_OPERATORS = [
  "not_contain",
  "not_match",
  "begins_with",
  "end_with",
  "contain",
  "match",
];

export class ConditionDslError extends Error {
  constructor(message, position) {
    super(position == null ? message : `${message} (at position ${position})`);
    this.name = "ConditionDslError";
    this.position = position;
  }
}

let uuidCounter = 0;

/** crypto.randomUUID where available; deterministic fallback keeps tests stable. */
function defaultIdFactory() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  uuidCounter += 1;
  return `dsl-${uuidCounter}`;
}

function isRuleGroup(node) {
  return node && typeof node === "object" && Array.isArray(node.rules);
}

/* ------------------------------------------------------------------ printer */

function printValue(rule) {
  const inputType = rule.inputType || getInputType(rule.field);
  const raw = rule.value == null ? "" : String(rule.value);
  return inputType === INPUT_TYPE.NUMERIC ? raw : JSON.stringify(raw);
}

function printRule(rule) {
  return `${rule.field} ${rule.operator} ${printValue(rule)}`;
}

/**
 * Reads the combinator out of a group's infix `rules` array. MedDream toggles
 * it for the whole group, so a group never mixes && and ||.
 */
export function getCombinator(group) {
  if (!isRuleGroup(group)) return COMBINATOR.AND;
  const found = group.rules.find((r) => typeof r === "string");
  return found === COMBINATOR.OR ? COMBINATOR.OR : COMBINATOR.AND;
}

/** Operands only, with the infix separator strings dropped. */
export function getOperands(group) {
  if (!isRuleGroup(group)) return [];
  return group.rules.filter((r) => typeof r !== "string");
}

function printNode(node, isTopLevel) {
  if (!isRuleGroup(node)) return printRule(node);

  const operands = getOperands(node);
  if (operands.length === 0) return isTopLevel ? TRUE_LITERAL : `(${TRUE_LITERAL})`;

  const joined = operands
    .map((operand) => printNode(operand, false))
    .join(` ${COMBINATOR_TO_DSL[getCombinator(node)]} `);

  if (node.not) return `!(${joined})`;
  // A lone operand already printed its own grouping; only multi-operand groups
  // need parens, and never at the top level.
  if (isTopLevel || operands.length === 1) return joined;
  return `(${joined})`;
}

/** Serialise a condition tree to MedDream's text form. */
export function printConditionTree(group) {
  if (!isRuleGroup(group)) return TRUE_LITERAL;
  return printNode(group, true);
}

/* ----------------------------------------------------------------- tokenizer */

function tokenize(input, knownFields) {
  // Longest-first so `instance.SamplesPerPixel[0028,0002)]` — which contains an
  // unbalanced ')' — is consumed whole instead of tripping paren matching.
  const fields = [...knownFields].sort((a, b) => b.length - a.length);
  const tokens = [];
  let i = 0;

  while (i < input.length) {
    const ch = input[i];

    if (/\s/.test(ch)) {
      i += 1;
      continue;
    }

    if (input.startsWith("&&", i) || input.startsWith("||", i)) {
      tokens.push({ type: "combinator", value: input.slice(i, i + 2), pos: i });
      i += 2;
      continue;
    }

    if (ch === "!" && input[i + 1] !== "=") {
      tokens.push({ type: "not", pos: i });
      i += 1;
      continue;
    }

    if (ch === "(") {
      tokens.push({ type: "lparen", pos: i });
      i += 1;
      continue;
    }

    if (ch === ")") {
      tokens.push({ type: "rparen", pos: i });
      i += 1;
      continue;
    }

    if (ch === '"') {
      let j = i + 1;
      let out = "";
      while (j < input.length && input[j] !== '"') {
        if (input[j] === "\\" && j + 1 < input.length) {
          out += input[j + 1];
          j += 2;
        } else {
          out += input[j];
          j += 1;
        }
      }
      if (j >= input.length) throw new ConditionDslError("Unterminated string", i);
      tokens.push({ type: "string", value: out, pos: i });
      i = j + 1;
      continue;
    }

    const field = fields.find((f) => input.startsWith(f, i));
    if (field) {
      tokens.push({ type: "field", value: field, pos: i });
      i += field.length;
      continue;
    }

    const symbol = SYMBOL_OPERATORS.find((op) => input.startsWith(op, i));
    if (symbol) {
      tokens.push({ type: "operator", value: symbol, pos: i });
      i += symbol.length;
      continue;
    }

    const numberMatch = /^-?\d+(\.\d+)?/.exec(input.slice(i));
    if (numberMatch) {
      tokens.push({ type: "number", value: numberMatch[0], pos: i });
      i += numberMatch[0].length;
      continue;
    }

    const wordMatch = /^[A-Za-z_][A-Za-z0-9_.[\],]*/.exec(input.slice(i));
    if (wordMatch) {
      const word = wordMatch[0];
      tokens.push({
        type: WORD_OPERATORS.includes(word) ? "operator" : "field",
        value: word,
        pos: i,
      });
      i += word.length;
      continue;
    }

    throw new ConditionDslError(`Unexpected character '${ch}'`, i);
  }

  return tokens;
}

/* -------------------------------------------------------------------- parser */

/**
 * Parse MedDream's text form into a condition tree.
 *
 * Unknown field names are accepted rather than rejected — MedDream's vocabulary
 * grows between versions, and refusing to parse an imported file over an
 * unrecognised field would be worse than carrying it through.
 */
export function parseConditionTree(text, options = {}) {
  const {
    idFactory = defaultIdFactory,
    knownFields = ALL_KNOWN_FIELDS,
    strictFields = false,
  } = options;

  const tokens = tokenize(String(text ?? ""), knownFields);
  let pos = 0;

  const peek = () => tokens[pos];
  const next = () => tokens[pos++];

  function makeGroup(operands, combinator, not = false) {
    const rules = [];
    operands.forEach((operand, index) => {
      if (index > 0) rules.push(combinator);
      rules.push(operand);
    });
    return { id: idFactory(), not, rules };
  }

  function parseExpression() {
    return parseOr();
  }

  function parseOr() {
    const operands = [parseAnd()];
    while (peek()?.type === "combinator" && peek().value === "||") {
      next();
      operands.push(parseAnd());
    }
    if (operands.length === 1) return operands[0];
    return makeGroup(operands, COMBINATOR.OR);
  }

  function parseAnd() {
    const operands = [parseUnary()];
    while (peek()?.type === "combinator" && peek().value === "&&") {
      next();
      operands.push(parseUnary());
    }
    if (operands.length === 1) return operands[0];
    return makeGroup(operands, COMBINATOR.AND);
  }

  function parseUnary() {
    if (peek()?.type === "not") {
      next();
      const inner = parseUnary();
      if (isRuleGroup(inner)) return { ...inner, not: true };
      return makeGroup([inner], COMBINATOR.AND, true);
    }
    return parsePrimary();
  }

  function parsePrimary() {
    const token = peek();
    if (!token) throw new ConditionDslError("Unexpected end of expression");

    if (token.type === "lparen") {
      next();
      const inner = parseExpression();
      if (peek()?.type !== "rparen") {
        throw new ConditionDslError("Expected ')'", peek()?.pos);
      }
      next();
      // Keep the paren as its own nesting level. MedDream stores a group whose
      // sole operand is another group and prints it with parens, so collapsing
      // the level here would drop them on the next print.
      return makeGroup([inner], COMBINATOR.AND);
    }

    // `1 == 1` — the always-true placeholder for an empty block.
    if (token.type === "number") {
      const start = pos;
      const left = next();
      const op = peek();
      if (op?.type === "operator" && op.value === "==") {
        next();
        const right = peek();
        if (right?.type === "number" && right.value === left.value) {
          next();
          return { id: idFactory(), not: false, rules: [] };
        }
      }
      pos = start;
      throw new ConditionDslError(`Unexpected value '${left.value}'`, left.pos);
    }

    if (token.type !== "field") {
      throw new ConditionDslError(`Expected a field name, got '${token.value ?? token.type}'`, token.pos);
    }
    next();

    if (strictFields && !knownFields.includes(token.value)) {
      throw new ConditionDslError(`Unknown field '${token.value}'`, token.pos);
    }

    const operator = peek();
    if (operator?.type !== "operator") {
      throw new ConditionDslError(`Expected an operator after '${token.value}'`, operator?.pos ?? token.pos);
    }
    next();

    const value = peek();
    if (!value || (value.type !== "string" && value.type !== "number" && value.type !== "field")) {
      throw new ConditionDslError(`Expected a value after '${operator.value}'`, value?.pos ?? operator.pos);
    }
    next();

    const inputType = value.type === "string" ? INPUT_TYPE.STRING : getInputType(token.value);

    return {
      id: idFactory(),
      field: token.value,
      operator: operator.value,
      value: String(value.value),
      valueSource: "value",
      path: getDefaultPath(token.value),
      inputType,
    };
  }

  if (tokens.length === 0) return { id: idFactory(), not: false, rules: [] };

  const parsed = parseExpression();
  if (pos < tokens.length) {
    throw new ConditionDslError(`Unexpected trailing input`, peek().pos);
  }

  // The document always stores a group at the top of a condition slot.
  return isRuleGroup(parsed) ? parsed : makeGroup([parsed], COMBINATOR.AND);
}
